/**
 * Utility functions for validating video duration
 */

export const VIDEO_CONSTRAINTS = {
  MAX_DURATION_SECONDS: 600, // 10 minutes
} as const;

/**
 * Get the duration of a video file in seconds
 * Loads only metadata without downloading the entire video
 */
export async function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      const duration = video.duration; // in seconds
      resolve(duration);
    };

    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error(`Failed to load video metadata for ${file.name}`));
    };

    video.src = URL.createObjectURL(file);
  });
}

export interface VideoDurationValidationResult {
  validFiles: File[];
  tooLongFiles: File[];
}

/**
 * Validate that video files don't exceed maximum duration
 * Checks all files in parallel for performance
 *
 * @param files - Array of video files to validate
 * @param maxDurationSeconds - Maximum allowed duration in seconds (default: 600 = 10 minutes)
 * @returns Object containing validFiles and tooLongFiles arrays
 */
export async function validateVideoDuration(
  files: File[],
  maxDurationSeconds: number = VIDEO_CONSTRAINTS.MAX_DURATION_SECONDS,
): Promise<VideoDurationValidationResult> {
  const validFiles: File[] = [];
  const tooLongFiles: File[] = [];

  await Promise.all(
    files.map(async (file) => {
      try {
        const duration = await getVideoDuration(file);
        if (duration <= maxDurationSeconds) {
          validFiles.push(file);
        } else {
          tooLongFiles.push(file);
        }
      } catch (error) {
        console.error(`Failed to get duration for ${file.name}:`, error);
        // Treat as invalid if we can't read duration
        // This handles corrupted files or unsupported formats
        tooLongFiles.push(file);
      }
    }),
  );

  return { validFiles, tooLongFiles };
}
